import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AdminKycCheck,
  AdminUserResponse,
  KycCheckType,
  DriverProfileResponse,
  ProfileChangeRequest,
  FleetManagerProfileResponse,
  ShipperProfileResponse,
} from '../../api/types';
import { ProfileDocuments } from './ProfileDocuments';
import { ProfileMediaPreview } from './ProfileMediaPreview';
import styles from './ProfileDocuments.module.css';
import { StatusPill } from '../ui/StatusPill';
import { kycStatusColor } from './statusColor';
import { StatusIcon } from '../ui/StatusIcon';
import { Button } from '../ui/Button';
import { ErrorState } from '../ui/AsyncState';
import { kycCheckDisplay } from './kycCheckDisplay';
import { ChangeRequestCard, ChangeRequestHistory } from './ChangeRequestCard';
import { currentProfileValues, sectionHost, type ChangeRequestHost } from './changeRequestDisplay';

/** Automatic PAN / bank check results for the PAN and banking sections (Contract C6). */
export interface KycChecksProp {
  data: AdminKycCheck[] | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => unknown;
  /** Given on the KYC tab only; the Approve dialog shows the badges read-only. */
  onOverride?: (check: AdminKycCheck) => void;
}

/** The user's profile change requests (#281); given on the KYC tab only. */
export interface ChangeRequestsProp {
  data: ProfileChangeRequest[] | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => unknown;
}

type Profile = DriverProfileResponse | FleetManagerProfileResponse | ShipperProfileResponse | null;
type Field = [string, string | number | null | undefined];

type SectionId =
  'personal' | 'business' | 'pan' | 'gst' | 'licence' | 'vehicle' | 'banking' | 'operational';

/** How each section locks once KYC is SUBMITTED or VERIFIED (Contract, dnm-work#144). */
type LockMode =
  | { kind: 'full' }
  | { kind: 'partial'; label: string }
  | { kind: 'never'; label: string }
  | { kind: 'banking' };

const SECTION_LOCKS: Record<SectionId, LockMode> = {
  personal: { kind: 'partial', label: 'Name locked' },
  business: { kind: 'partial', label: 'Business name locked' },
  pan: { kind: 'full' },
  gst: { kind: 'full' },
  licence: { kind: 'full' },
  vehicle: { kind: 'never', label: 'Vehicle Assignment (Free)' },
  banking: { kind: 'banking' },
  operational: { kind: 'never', label: 'Editable' },
};

interface SectionLock {
  label: string;
  /** Status whose colour the badge uses; undefined = editable (gray). */
  status?: 'VERIFIED' | 'SUBMITTED';
}

const EDITABLE: SectionLock = { label: 'Editable' };

function lockingStatus(status: string | undefined): SectionLock['status'] {
  return status === 'VERIFIED' || status === 'SUBMITTED' ? status : undefined;
}

function fullLock(status: SectionLock['status']): SectionLock {
  if (status === 'VERIFIED') return { label: 'Verified · Locked', status };
  if (status === 'SUBMITTED') return { label: 'Under review · Locked', status };
  return EDITABLE;
}

function getSectionLock(
  id: SectionId,
  userKycStatus: string | undefined,
  partnerVerificationStatus?: string,
): SectionLock {
  const mode = SECTION_LOCKS[id];
  const status = lockingStatus(userKycStatus);
  switch (mode.kind) {
    case 'never':
      return { label: mode.label };
    case 'banking': {
      const partner = lockingStatus(partnerVerificationStatus);
      if (partner === 'VERIFIED' || status === 'VERIFIED') return fullLock('VERIFIED');
      return fullLock(partner ?? status);
    }
    case 'partial':
      return status ? { label: mode.label, status } : EDITABLE;
    case 'full':
      return fullLock(status);
  }
}

const CHECK_LABEL: Record<KycCheckType, string> = { PAN: 'PAN', BANK: 'bank' };

/** One check row under a section header. Badges inform ops and gate nothing (R28). */
function CheckRow({ type, checks }: { type: KycCheckType; checks: KycChecksProp }) {
  const { t } = useTranslation();
  const testId = `check-badge-${type.toLowerCase()}`;
  if (checks.isLoading) {
    return type === 'PAN' ? (
      <p className={styles.checkPending} data-testid={testId}>
        {t('domain.kycReviewContent.checking')}
      </p>
    ) : null;
  }
  if (checks.isError) {
    // One error for both checks, in the PAN section; the rest of the tab keeps working.
    return type === 'PAN' ? (
      <div className={styles.checkRow} data-testid={testId}>
        <ErrorState title={t('domain.kycReviewContent.checksError')} onRetry={checks.refetch} />
      </div>
    ) : null;
  }
  const check = checks.data?.find((c) => c.checkType === type);
  if (!check) return null;
  const display = kycCheckDisplay(check);
  return (
    <div className={styles.checkRow} data-testid={testId}>
      <div className={styles.checkPills}>
        {display.pills.map((p) => (
          <StatusPill
            key={p.label}
            bg={p.bg}
            text={p.text}
            label={p.label}
            icon={p.icon ? <StatusIcon name={p.icon} /> : undefined}
          />
        ))}
        {checks.onOverride && display.canOverride && (
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Override ${CHECK_LABEL[type]} check`}
            onClick={() => checks.onOverride?.(check)}
          >
            {t('domain.kycReviewContent.override')}
          </Button>
        )}
      </div>
      {display.detail && <p className={styles.checkDetail}>{display.detail}</p>}
    </div>
  );
}

const SECTION_DOM_IDS: Partial<Record<SectionId, string>> = { banking: 'banking-information' };

export function KycReviewContent({
  user,
  profile,
  onEdit,
  checks,
  changeRequests,
  canDecide = true,
}: {
  user: AdminUserResponse;
  profile: Profile;
  /** Omitted → no section or document edit buttons (an ops executive, R67). */
  onEdit?: (focusId?: string) => void;
  /** False → change requests show without Approve / Reject (an ops executive, R69). */
  canDecide?: boolean;
  /** PAN / bank check results; omitted → no check rows (e.g. drivers). */
  checks?: KycChecksProp;
  /** Profile change requests; omitted → no cards (e.g. inside the Approve KYC dialog). */
  changeRequests?: ChangeRequestsProp;
}) {
  const { t } = useTranslation();
  const driver = user.role === 'DRIVER' ? (profile as DriverProfileResponse | null) : null;
  const business =
    user.role !== 'DRIVER'
      ? (profile as FleetManagerProfileResponse | ShipperProfileResponse | null)
      : null;
  const partner =
    user.role === 'FLEET_MANAGER' ? (profile as FleetManagerProfileResponse | null) : null;
  const address = business?.billingAddress;
  const addressText =
    typeof address === 'string'
      ? address
      : address
        ? [address.line, address.city, address.state, address.pincode].filter(Boolean).join(', ')
        : business && 'billingAddressText' in business
          ? business.billingAddressText
          : null;
  const document = (label: string) => (
    <ProfileDocuments
      userId={user.id}
      profile={profile}
      role={user.role}
      documentLabels={[label]}
      onEditDocument={onEdit}
      autoLoad
      embedded
    />
  );
  // Change requests (#281): PENDING as a diff card, decided as history, on the section they change.
  const requests = changeRequests?.data ?? [];
  const current = currentProfileValues(user, profile);
  const hostOf = (r: ProfileChangeRequest): ChangeRequestHost | null =>
    sectionHost(r.section, user.role);
  const requestsBlock = (list: ProfileChangeRequest[]) => {
    if (list.length === 0) return null;
    const pending = list.filter((r) => r.status === 'PENDING');
    const decided = list.filter((r) => r.status !== 'PENDING');
    return (
      <>
        {pending.map((r) => (
          <ChangeRequestCard
            key={r.id}
            userId={user.id}
            request={r}
            current={current}
            kycStatus={user.kycStatus}
            canDecide={canDecide}
          />
        ))}
        <ChangeRequestHistory requests={decided} />
      </>
    );
  };
  const requestsFor = (id: SectionId) =>
    requestsBlock(requests.filter((r) => hostOf(r) === id));
  const unplaced = requests.filter((r) => hostOf(r) === null);
  const section = (
    id: SectionId,
    title: string,
    fields: Field[],
    edit: { label: string; focusId: string } | null,
    media?: ReactNode,
    checkType?: KycCheckType,
  ) => {
    const lock = getSectionLock(id, user.kycStatus, partner?.verificationStatus);
    const colour = kycStatusColor(lock.status ?? 'PENDING');
    return (
      <section className={styles.card} aria-label={title} id={SECTION_DOM_IDS[id]}>
        <div className={styles.cardHeader}>
          <h4>{title}</h4>
          <span data-testid={`lock-badge-${id}`}>
            <StatusPill bg={colour.bg} text={colour.text} label={lock.label} />
          </span>
        </div>
        {checkType && checks && user.role !== 'DRIVER' && (
          <CheckRow type={checkType} checks={checks} />
        )}
        <div className={media ? styles.reviewPair : undefined}>
          <div>
            <dl className={styles.fields}>
              {fields.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>
                    {value === null || value === undefined || value === '' ? 'Not provided' : value}
                  </dd>
                </div>
              ))}
            </dl>
            {edit && onEdit && (
              <button type="button" onClick={() => onEdit(edit.focusId)}>
                {edit.label}
              </button>
            )}
          </div>
          {media}
        </div>
        {requestsFor(id)}
      </section>
    );
  };
  const photo = user.avatarUrl ? (
    <ProfileMediaPreview
      key={user.avatarUrl}
      userId={user.id}
      url={user.avatarUrl}
      // eslint-disable-next-line no-restricted-syntax -- sentinel, not copy: ProfileMediaPreview/ProfileDocuments switch on label === 'Profile photo'
      label="Profile photo"
      autoLoad
      compact
    />
  ) : (
    <div>
      <h4>{t('domain.kycReviewContent.photoOptional')}</h4>
      <div className={styles.photoPlaceholder}>{t('domain.kycReviewContent.noPhoto')}</div>
    </div>
  );
  return (
    <div className={styles.grid}>
      {changeRequests?.isError && (
        <ErrorState title={t('domain.kycReviewContent.changeRequestsError')} onRetry={changeRequests.refetch} />
      )}
      {unplaced.length > 0 && (
        <section className={styles.card} aria-label={t('domain.kycReviewContent.changeRequests')}>
          <div className={styles.cardHeader}>
            <h4>{t('domain.kycReviewContent.changeRequests')}</h4>
          </div>
          {requestsBlock(unplaced)}
        </section>
      )}
      {section(
        'personal',
        'Personal details',
        [
          ['Name', user.fullName],
          ['Phone number', user.phoneNumber],
          ['Email', user.email],
        ],
        { label: 'Edit personal details', focusId: 'profile-full-name' },
        photo,
      )}
      {user.role === 'DRIVER' ? (
        <>
          {section(
            'licence',
            'Driving licence',
            [
              ['DL number', driver?.licenseNumber],
              ['DL expiry', driver?.licenseExpiry],
            ],
            { label: 'Edit licence details', focusId: 'driver-license' },
            document('DL'),
          )}
          {section(
            'vehicle',
            'Vehicle registration',
            [['RC / truck number', driver?.vehicleNumber]],
            { label: 'Edit registration details', focusId: 'driver-truck' },
            document('RC'),
          )}
          {section(
            'operational',
            'Operational',
            [['Availability', driver ? (driver.isAvailable ? 'Available' : 'Unavailable') : null]],
            // The edit modal (UserFormModal) has no availability field, so no edit action here.
            null,
          )}
        </>
      ) : (
        <>
          {section(
            'business',
            'Business details',
            [
              ['Business name', business?.companyName],
              ['Secondary phone', business?.secondaryPhoneNumber],
              ['WhatsApp number', business?.whatsappNumber],
              ['Billing address', addressText],
              ...(user.role === 'FLEET_MANAGER'
                ? ([
                    ['Fleet size', partner?.fleetSize],
                    ['Service regions', partner?.serviceRegions],
                  ] as Field[])
                : []),
            ],
            { label: 'Edit business details', focusId: 'profile-company' },
          )}
          {section(
            'pan',
            'PAN details',
            [['PAN number', business?.panNumber]],
            { label: 'Edit PAN details', focusId: 'profile-pan' },
            document('PAN'),
            'PAN',
          )}
          {section(
            'gst',
            'GST details',
            [['GST number', business?.gstin]],
            { label: 'Edit GST details', focusId: 'profile-gstin' },
            document('GST'),
          )}
          {user.role === 'FLEET_MANAGER' &&
            section(
              'banking',
              'Banking information',
              [
                ['Bank name', partner?.bankName],
                ['Account holder name', partner?.accountHolderName],
                ['Account number', partner?.accountNumber],
                ['IFSC code', partner?.ifscCode],
                ['Bank proof type', partner?.bankProofType],
              ],
              { label: 'Edit banking information', focusId: 'profile-bank-name' },
              document('Bank proof'),
              'BANK',
            )}
        </>
      )}
    </div>
  );
}
